import { Column } from 'react-table';
import { TableHeader, UserAvatar } from 'redesign';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { IconNocircleCheckApproved } from 'assets/img';
import { useTranslation } from 'react-i18next';
import {
  ShiftDetailBaseColumnDefinition,
  ShiftDetailCellBaseProps,
} from '../types/shift-detail-tables.types';
import { getFlexWorkerLabel } from '../../../../../../utils/get-flex-worker-label';

export const useGetShiftDetailsColumnDefinitionBase: () => Column<ShiftDetailBaseColumnDefinition>[] =
  () => {
    const { t } = useTranslation('hq-screen-specific');
    const flexWorkerImage: Column<ShiftDetailBaseColumnDefinition> = {
      Header: '',
      accessor: 'flexWorkerImage',
      Cell: FlexWorkerImageCell,
      width: '130px',
    };
    const fw: Column<ShiftDetailBaseColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.table_headers.flexworker', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'flexWorkerFullName',
      Cell: FlexWorkerCell,
    };

    return [flexWorkerImage, fw];
  };

const FlexWorkerImageCell = ({
  value,
  onProfileClick,
}: ShiftDetailCellBaseProps<'flexWorkerImage'>) => {
  const { t } = useTranslation('hq-screen-specific');
  const {
    id,
    firstName,
    lastName,
    imageUrl,
    relation,
    employmentType,
    hasCandidateLevel,
    isReplacement,
    lenderCompanyName,
    includeCheckedInIndicator,
  } = value;

  const label = getFlexWorkerLabel({
    employmentType,
    hasCandidateLevel,
    isReplacement,
    lenderCompanyName,
  });

  const checkedInTooltipTitle = includeCheckedInIndicator.visible
    ? t('shifts.placements.checked_in_indicator', {
        dateTime: includeCheckedInIndicator.dateTime,
        ns: 'hq-screen-specific',
      })
    : '';

  return (
    <AvatarWithBadgeContainer>
      <UserAvatar
        onClick={() => onProfileClick(id, relation)}
        firstName={firstName}
        lastName={lastName}
        imageUrl={imageUrl}
        avatarLabel={label}
      />
      {includeCheckedInIndicator.visible ? (
        <Tooltip title={checkedInTooltipTitle}>
          <ApprovedBadgeIconBox
            data-testid="flex-worker-checked-in-badge"
            onClick={(event) => event.stopPropagation()}
          >
            <StyledApprovedIcon as={IconNocircleCheckApproved} width={20} height={20} />
          </ApprovedBadgeIconBox>
        </Tooltip>
      ) : null}
    </AvatarWithBadgeContainer>
  );
};

const AvatarWithBadgeContainer = styled(Box)(() => ({
  position: 'relative',
  display: 'inline-flex',
  justifyContent: 'center',
}));

const StyledApprovedIcon = styled('svg')(({ theme }) => ({
  '& path': {
    stroke: theme.palette.lwPrimaryAccentLight[100],
  },
}));

const ApprovedBadgeIconBox = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '20px',
  height: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 101,
}));

const FlexWorkerCell = ({ value }: ShiftDetailCellBaseProps<'flexWorkerFullName'>) => {
  return (
    <Typography noWrap style={{ maxWidth: '240px' }}>
      {value}
    </Typography>
  );
};
